import React, { useState, useEffect, useReducer, useCallback } from "react";
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Grid,
  Tooltip,
  IconButton,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import CheckIcon from "@material-ui/icons/Check";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import WarningIcon from "@material-ui/icons/Warning";
import { toast } from "react-toastify";
import moment from "moment";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import FinanceSummaryCard from "../../components/FinanceSummaryCard";
import MobileRecordCard, { MobileRecordCardSkeleton } from "../../components/MobileRecordCard";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  confirmBtn: {
    fontWeight: 700,
    fontSize: "0.75rem",
    borderRadius: 6,
    backgroundColor: "#2e7d32",
    color: "#fff",
    "&:hover": { backgroundColor: "#1b5e20" },
  },
}));

const fmtBRL = (v) =>
  Number(v || 0).toLocaleString("pt-br", { style: "currency", currency: "BRL" });

const reducer = (state, action) => {
  if (action.type === "LOAD") {
    const next = [...state];
    action.payload.forEach((item) => {
      const idx = next.findIndex((a) => a.id === item.id);
      if (idx !== -1) next[idx] = item;
      else next.push(item);
    });
    return next;
  }
  if (action.type === "REMOVE") return state.filter((a) => a.id !== action.payload);
  if (action.type === "RESET") return [];
  return state;
};

// ── Modal de confirmação de pagamento ──────────────────────────────────────

const ConfirmPaymentModal = ({ open, appointment, onClose, onConfirmed }) => {
  const [methods, setMethods] = useState([]);
  const [methodId, setMethodId] = useState("");
  const [paidAt, setPaidAt] = useState(moment().format("YYYY-MM-DDTHH:mm"));
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/payment-methods").then(({ data }) => {
      setMethods(data);
      const def = data.find((m) => m.isDefault);
      if (def) setMethodId(def.id);
    });
    setPaidAt(moment().format("YYYY-MM-DDTHH:mm"));
    setNotes("");
  }, [open]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await api.post(
        `/transactions/appointments/${appointment.id}/confirm-payment`,
        { paymentMethodId: methodId || undefined, paidAt, notes: notes || undefined }
      );
      toast.success("Pagamento confirmado!");
      onConfirmed(appointment);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  if (!appointment) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Confirmar Pagamento</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" gutterBottom>
          <strong>Agendamento #{appointment.id}</strong>
          {appointment.contact && ` — ${appointment.contact.name}`}
          {appointment.service && ` | ${appointment.service.name}`}
        </Typography>
        <Typography variant="h6" style={{ color: "#2e7d32", marginBottom: 16 }}>
          {fmtBRL(appointment.price)}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Forma de Pagamento</InputLabel>
              <Select value={methodId} onChange={(e) => setMethodId(e.target.value)}>
                <MenuItem value=""><em>Não informado</em></MenuItem>
                {methods.map((m) => (
                  <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Data/hora do pagamento"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Observações"
              fullWidth
              multiline
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button
          variant="contained"
          style={{ backgroundColor: "#2e7d32", color: "#fff" }}
          onClick={handleConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
        >
          Confirmar Recebimento
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Painel principal ────────────────────────────────────────────────────────

const ContasReceberPanel = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [appointments, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalValue, setTotalValue] = useState(0);

  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async (page = 1, reset = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/transactions/accounts-receivable", {
        params: { pageNumber: page }
      });
      if (reset) dispatch({ type: "RESET" });
      dispatch({ type: "LOAD", payload: data.appointments });
      setHasMore(data.hasMore);
      setTotalValue(data.totalValue);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    load(1, true);
  }, [load]);

  useEffect(() => {
    if (pageNumber > 1) load(pageNumber);
  }, [pageNumber, load]);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
  };

  const handleConfirmed = (appointment) => {
    dispatch({ type: "REMOVE", payload: appointment.id });
    setTotalValue((prev) => prev - Number(appointment?.price ?? 0));
  };

  const overdueCount = appointments.filter(
    (a) => moment(a.startAt).isBefore(moment(), "day")
  ).length;

  return (
    <Paper className={classes.root} variant="outlined" onScroll={handleScroll}>
      <ConfirmPaymentModal
        open={modalOpen}
        appointment={selected}
        onClose={() => { setModalOpen(false); setSelected(null); }}
        onConfirmed={handleConfirmed}
      />

      {/* Cabeçalho com ajuda */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          Contas a Receber
        </Typography>
        <Tooltip
          title={
            <div style={{ fontSize: "0.82rem", lineHeight: 1.6, maxWidth: 300 }}>
              <strong>Como funciona este relatório?</strong>
              <br /><br />
              Lista todos os agendamentos com status <strong>Concluído</strong> e valor acima de R$ 0,00 que ainda <strong>não possuem pagamento confirmado</strong>.
              <br /><br />
              • <strong>Lançado</strong>: já existe um lançamento pendente gerado automaticamente ao concluir o agendamento.<br />
              • <strong>Sem lançamento</strong>: agendamento antigo, anterior à ativação do módulo financeiro.<br /><br />
              Em ambos os casos, clique em <strong>Recebido</strong> para confirmar o pagamento e remover o item desta lista.
            </div>
          }
          arrow
          interactive
          placement="right"
        >
          <IconButton size="small" style={{ marginLeft: 6, color: "#9e9e9e" }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {/* Resumo */}
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={6} md={4}>
          <FinanceSummaryCard
            label="Total a receber"
            value={fmtBRL(totalValue)}
            caption={`${appointments.length} agendamento${appointments.length !== 1 ? "s" : ""} sem pagamento`}
            icon={<AttachMoneyIcon />}
            tone="green"
            colorValue
            loading={loading && appointments.length === 0}
          />
        </Grid>
        {overdueCount > 0 && (
          <Grid item xs={12} sm={6} md={4}>
            <FinanceSummaryCard
              label="Atrasados"
              value={`${overdueCount}`}
              caption={`agendamento${overdueCount !== 1 ? "s" : ""} com data passada`}
              icon={<WarningIcon />}
              tone="red"
              colorValue
            />
          </Grid>
        )}
      </Grid>

      {/* Lista: cards no mobile, tabela no desktop */}
      {isMobile ? (
        <div>
          {loading && appointments.length === 0 &&
            [0, 1, 2].map((i) => <MobileRecordCardSkeleton key={i} />)}

          {!loading && appointments.length === 0 && (
            <Typography align="center" style={{ padding: 32, color: "#9e9e9e" }}>
              Nenhuma conta a receber. Todos os pagamentos estão em dia!
            </Typography>
          )}

          {appointments.map((appt) => {
            const isLate = moment(appt.startAt).isBefore(moment(), "day");
            return (
              <MobileRecordCard
                key={appt.id}
                title={appt.contact?.name ?? "—"}
                subtitle={appt.contact?.number}
                highlight={isLate}
                rows={[
                  { label: "Serviço", value: appt.service?.name ?? "—" },
                  { label: "Profissional", value: appt.user?.name ?? "—" },
                  {
                    label: "Data",
                    value: `${moment(appt.startAt).format("DD/MM/YYYY HH:mm")}${isLate ? " · Atrasado" : ""}`,
                  },
                  {
                    label: "Valor",
                    value: (
                      <span style={{ fontWeight: 700, color: "#2e7d32" }}>
                        {fmtBRL(appt.price)}
                      </span>
                    ),
                  },
                ]}
                chips={
                  appt.pendingTransaction ? (
                    <Chip
                      label="Lançado"
                      size="small"
                      style={{ backgroundColor: "#fff8e1", color: "#f57f17", fontWeight: 600, borderRadius: 8, height: 22, fontSize: "0.72rem" }}
                    />
                  ) : (
                    <Chip
                      label="Sem lançamento"
                      size="small"
                      style={{ backgroundColor: "#f5f5f5", color: "#9e9e9e", borderRadius: 8, height: 22, fontSize: "0.72rem" }}
                    />
                  )
                }
                footer={
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    disableElevation
                    className={classes.confirmBtn}
                    startIcon={<CheckIcon style={{ fontSize: 14 }} />}
                    onClick={() => { setSelected(appt); setModalOpen(true); }}
                  >
                    Recebido
                  </Button>
                }
              />
            );
          })}
        </div>
      ) : (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Cliente</TableCell>
            <TableCell>Serviço</TableCell>
            <TableCell>Profissional</TableCell>
            <TableCell align="center">Data</TableCell>
            <TableCell align="right">Valor</TableCell>
            <TableCell align="center">Status Tx</TableCell>
            <TableCell align="center">Ação</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {appointments.map((appt) => {
            const isLate = moment(appt.startAt).isBefore(moment(), "day");
            return (
              <TableRow key={appt.id} style={isLate ? { backgroundColor: "rgba(255,235,235,0.6)" } : {}}>
                <TableCell style={{ color: "#9e9e9e", fontSize: "0.75rem" }}>#{appt.id}</TableCell>
                <TableCell>
                  <Typography variant="body2">{appt.contact?.name ?? "—"}</Typography>
                  {appt.contact?.number && (
                    <Typography variant="caption" color="textSecondary">{appt.contact.number}</Typography>
                  )}
                </TableCell>
                <TableCell>{appt.service?.name ?? "—"}</TableCell>
                <TableCell>{appt.user?.name ?? "—"}</TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{moment(appt.startAt).format("DD/MM/YYYY")}</Typography>
                  <Typography variant="caption" style={{ color: isLate ? "#c62828" : "#9e9e9e" }}>
                    {moment(appt.startAt).format("HH:mm")}
                    {isLate && " · Atrasado"}
                  </Typography>
                </TableCell>
                <TableCell align="right" style={{ fontWeight: 700, color: "#2e7d32" }}>
                  {fmtBRL(appt.price)}
                </TableCell>
                <TableCell align="center">
                  {appt.pendingTransaction ? (
                    <Chip
                      label="Lançado"
                      size="small"
                      style={{ backgroundColor: "#fff8e1", color: "#f57f17", fontWeight: 600, borderRadius: 8, height: 22, fontSize: "0.72rem" }}
                    />
                  ) : (
                    <Chip
                      label="Sem lançamento"
                      size="small"
                      style={{ backgroundColor: "#f5f5f5", color: "#9e9e9e", borderRadius: 8, height: 22, fontSize: "0.72rem" }}
                    />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    className={classes.confirmBtn}
                    startIcon={<CheckIcon style={{ fontSize: 14 }} />}
                    onClick={() => { setSelected(appt); setModalOpen(true); }}
                  >
                    Recebido
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          {loading && <TableRowSkeleton columns={8} />}
          {!loading && appointments.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} align="center" style={{ padding: 32, color: "#9e9e9e" }}>
                Nenhuma conta a receber. Todos os pagamentos estão em dia!
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      )}
    </Paper>
  );
};

export default ContasReceberPanel;
